import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Edit, Trash2, RefreshCw, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ActionsMenuProps {
  onModify: () => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onCheckStatus?: () => void;
  status?: string;
  className?: string;
  menuPosition?: 'top' | 'bottom';
  isAnyProcessing?: boolean;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  onModify,
  onDelete,
  onDuplicate,
  onCheckStatus,
  status,
  className = '',
  menuPosition = 'bottom',
  isAnyProcessing = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate position when menu opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 173; // 20% wider than 144px
      
      // Always position below the button regardless of menuPosition prop
      setMenuStyle({
        position: 'fixed',
        left: `${buttonRect.left - menuWidth + buttonRect.width}px`,
        top: `${buttonRect.bottom + 8}px`,
        width: `${menuWidth}px`,
        zIndex: 60
      });
    }
  }, [isOpen, menuPosition]);

  const hasCheckStatusHandler = Boolean(onCheckStatus);
  const isProcessing = Boolean(isAnyProcessing);
  
  if (!onModify && !onDelete && !onDuplicate && !onCheckStatus) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click event
          setIsOpen(!isOpen);
        }}
        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Actions"
      >
        <MoreVertical className="w-5 h-5 text-gray-500" />
      </button>

      {isOpen && (
        <div style={menuStyle} className="rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="py-1">
            {isProcessing && onCheckStatus && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  onCheckStatus();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
              >
                <RefreshCw className="w-4 h-4 text-blue-500" />
                <span>{t('common.checkPostStatus', 'Check Post Status')}</span>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click event
                onModify();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Edit className="w-4 h-4 text-gray-500" />
              <span>{t('common.modify')}</span>
            </button>
            {onDuplicate && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  onDuplicate();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Copy className="w-4 h-4 text-gray-500" />
                <span>{t('common.duplicate', 'Duplicate')}</span>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click event
                onDelete();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
              <span>{t('common.delete')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 